import axiosInstance from './axiosConfig';
import { Permission, extractPermissionsFromToken } from '../utils/jwt';

interface LoginCredentials {
  username: string;
  password: string;
  rememberMe?: boolean;
  trustedDeviceToken?: string;
}

const REMEMBER_ME_KEY = 'rememberMe';
const TRUSTED_DEVICE_KEY = 'trustedDeviceToken';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  companyEmail: string;
  personalNumber: string;
  country_code?: string;
  office?: {
    _id: string;
    name: string;
    office_id: string;
    entity_id?: string | null;
    terminal_reader_id?: string;
    country?: string;
  } | string;
  permissionRole: string;
  position?: string;
  permissions?: Permission[];
  status: string;
  timeZone: string;
  pageAccess: string[];
  profileImageUrl?: string;
  commitment?: string;
  tableSettings: {
    [tableName: string]: {
      visibleColumns: string[];
    };
  };
  ledgerCompanyIds?: string[];
  ledgerPageAccess?: string[];
  sandboxAccess?: boolean;
  stripe_connect_allowed?: boolean;
  emailMfaEnabled?: boolean;
  totpEnabled?: boolean;
}

interface AuthResponse {
  token: string;
  user: User;
  trustedDeviceToken?: string;
}

export interface MfaRequiredResponse {
  requiresMfa: true;
  mfaToken: string;
  mfaMethod: 'email' | 'totp';
  maskedEmail: string | null;
}

export type LoginResponse = AuthResponse | MfaRequiredResponse;

export interface MfaStatus {
  emailMfaEnabled: boolean;
  totpEnabled: boolean;
  mfaMethod: 'none' | 'email' | 'totp';
  /** Full personal email — only returned on authenticated /mfa/status. */
  email: string | null;
  /** Masked form kept for compatibility; prefer `email` in profile UI. */
  maskedEmail: string | null;
  trustedDeviceCount: number;
}

export interface TotpSetupResponse {
  secret: string;
  qrCodeDataUrl: string;
  manualKey: string;
  otpauthUrl: string;
}

function isMfaRequired(data: LoginResponse): data is MfaRequiredResponse {
  return 'requiresMfa' in data && data.requiresMfa === true;
}

function persistSession(
  token: string,
  user: User,
  options?: { rememberMe?: boolean; trustedDeviceToken?: string },
) {
  const permissions = extractPermissionsFromToken(token);
  const userWithPermissions = { ...user, permissions };

  localStorage.setItem('token', token);
  if (options?.rememberMe) {
    localStorage.setItem(REMEMBER_ME_KEY, 'true');
  } else {
    localStorage.removeItem(REMEMBER_ME_KEY);
  }
  // Trust-device token is independent of stay-signed-in; only set when newly issued.
  if (options?.trustedDeviceToken) {
    localStorage.setItem(TRUSTED_DEVICE_KEY, options.trustedDeviceToken);
  }

  return { token, user: userWithPermissions };
}

class AuthService {
  public token: string | null = null;
  private lastCheck: number = 0;
  private readonly CHECK_INTERVAL = 60000;

  constructor() {
    this.token = localStorage.getItem('token');
  }

  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const trustedDeviceToken =
      credentials.trustedDeviceToken || localStorage.getItem(TRUSTED_DEVICE_KEY) || undefined;

    const response = await axiosInstance.post<LoginResponse>('/auth/login', {
      ...credentials,
      trustedDeviceToken,
    });

    const data = response.data;
    if (isMfaRequired(data)) {
      return data;
    }

    const { token, user, trustedDeviceToken: newTrustedToken } = data;
    this.token = token;
    persistSession(token, user, {
      rememberMe: credentials.rememberMe,
      trustedDeviceToken: newTrustedToken,
    });
    return data;
  }

  async verifyMfa(params: {
    mfaToken: string;
    code: string;
    rememberMe?: boolean;
    trustDevice?: boolean;
  }): Promise<AuthResponse> {
    const response = await axiosInstance.post<AuthResponse>('/auth/verify-mfa', params);
    const { token, user, trustedDeviceToken } = response.data;
    this.token = token;
    persistSession(token, user, {
      rememberMe: params.rememberMe,
      trustedDeviceToken,
    });
    return response.data;
  }

  async resendMfa(mfaToken: string): Promise<void> {
    await axiosInstance.post('/auth/resend-mfa', { mfaToken });
  }

  async getMfaStatus(): Promise<MfaStatus> {
    const response = await axiosInstance.get<MfaStatus>('/auth/mfa/status');
    return response.data;
  }

  async enableEmailMfa(): Promise<void> {
    await axiosInstance.post('/auth/mfa/email/enable');
    localStorage.removeItem(TRUSTED_DEVICE_KEY);
  }

  async disableEmailMfa(password: string): Promise<void> {
    await axiosInstance.post('/auth/mfa/email/disable', { password });
  }

  async startTotpSetup(): Promise<TotpSetupResponse> {
    const response = await axiosInstance.post<TotpSetupResponse>('/auth/mfa/totp/setup');
    return response.data;
  }

  async enableTotp(code: string): Promise<void> {
    await axiosInstance.post('/auth/mfa/totp/enable', { code });
    localStorage.removeItem(TRUSTED_DEVICE_KEY);
  }

  async disableTotp(password: string): Promise<void> {
    await axiosInstance.post('/auth/mfa/totp/disable', { password });
  }

  async startMfaStepUp(): Promise<{
    mfaMethod: 'email' | 'totp';
    maskedEmail: string | null;
    message?: string;
  }> {
    const response = await axiosInstance.post('/auth/mfa/step-up/start');
    return response.data;
  }

  async verifyMfaStepUp(code: string): Promise<{ elevated: boolean; expiresAt: string }> {
    const response = await axiosInstance.post('/auth/mfa/step-up/verify', { code });
    return response.data;
  }

  async resendMfaStepUp(): Promise<void> {
    await axiosInstance.post('/auth/mfa/step-up/resend');
  }

  async getMfaStepUpStatus(): Promise<{ elevated: boolean; expiresAt?: string }> {
    const response = await axiosInstance.get('/auth/mfa/step-up/status');
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      if (this.token) {
        await axiosInstance.post('/auth/logout');
      }
    } catch (error) {
      console.error('AuthService: Logout error:', error);
    } finally {
      this.token = null;
      localStorage.removeItem('token');
      localStorage.removeItem(REMEMBER_ME_KEY);
      // Keep trusted-device token across logout so MFA can still be skipped until it expires.
    }
  }

  getToken(): string | null {
    const token = localStorage.getItem('token');
    this.token = token;
    return token;
  }

  async checkAuth(): Promise<boolean> {
    const now = Date.now();
    if (now - this.lastCheck < this.CHECK_INTERVAL) {
      return this.isAuthenticated();
    }

    try {
      await axiosInstance.get('/auth/me');
      this.lastCheck = now;
      return true;
    } catch {
      this.token = null;
      localStorage.removeItem('token');
      return false;
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  }

  async getCurrentUser(forceRefresh: boolean = false): Promise<User> {
    const url = forceRefresh ? `/auth/me?_t=${Date.now()}&_r=${Math.random()}` : '/auth/me';
    const response = await axiosInstance.get<User>(url);
    const token = this.getToken();
    const permissions = extractPermissionsFromToken(token);
    return { ...response.data, permissions };
  }
}

export const authService = new AuthService();
export const getRememberMePreference = (): boolean => localStorage.getItem(REMEMBER_ME_KEY) === 'true';
export type { User, LoginCredentials, AuthResponse };
